import {
  Box,
  Circle,
  Flex,
  Float,
  HStack,
  Icon,
  IconButton,
  Menu,
  Portal,
  SegmentGroup,
  Spacer,
} from '@chakra-ui/react';
import { useNavigate } from '@tanstack/react-router';
import { t } from 'i18next';
import { useMemo } from 'react';
import { BiTask } from 'react-icons/bi';
import { FaSortAlphaDown, FaSortAlphaUpAlt } from 'react-icons/fa';
import { GoGoal } from 'react-icons/go';
import { MdSort } from 'react-icons/md';

import { SearchInput } from '@/components/common/SearchInput';
import { Route } from '@/routes/_layout/$slug/library.tsx';

import { actionColumnsDef, indicatorColumnsDef } from './LibraryData';
import LibraryFilterDrawer from './LibraryFilterDrawer';

const IndicatorsSortMenu = () => {
  const navigate = useNavigate({ from: Route.fullPath });
  const { type, sort } = Route.useSearch();

  const parsedSorting = useMemo(() => {
    return sort ? [{ id: sort.split(':')[0], desc: sort.split(':')[1] === 'desc' }] : [];
  }, [sort]);

  const columnsDef = type === 'actions' ? actionColumnsDef : indicatorColumnsDef;
  const columns = columnsDef.filter((c) => c.sortable);

  const getOrder = (accessorKey: string) => {
    const sortColumn = parsedSorting.find((s) => s.id === accessorKey);
    if (sortColumn) {
      return sortColumn.desc ? 'desc' : 'asc';
    }
    return undefined;
  };

  return (
    <Menu.Root closeOnSelect={false}>
      <Menu.Trigger asChild>
        <IconButton variant={'ghost'}>
          <MdSort />
          {sort !== '' && (
            <Float offset="1">
              <Circle size="4" bg="primary" color="white" fontSize={'xs'}>
                1
              </Circle>
            </Float>
          )}
        </IconButton>
      </Menu.Trigger>
      <Portal>
        <Menu.Positioner>
          <Menu.Content>
            <Menu.ItemGroup title={t('ui.sort')}>
              {columns.map(({ header, accessorKey }) => (
                <Menu.Item
                  key={accessorKey}
                  value={accessorKey}
                  onClick={() => {
                    navigate({
                      search: (prev: Record<string, string>) => {
                        const sortOrder = getOrder(accessorKey);
                        if (sortOrder === 'desc') {
                          return {
                            ...prev,
                            sort: '',
                          };
                        }
                        return {
                          ...prev,
                          sort: `${accessorKey}:${sortOrder === 'asc' ? 'desc' : 'asc'}`,
                        };
                      },
                    });
                  }}
                >
                  {typeof header === 'function' ? header() : header}
                  {getOrder(accessorKey) === 'asc' && (
                    <Icon>
                      <FaSortAlphaDown />
                    </Icon>
                  )}
                  {getOrder(accessorKey) === 'desc' && (
                    <Icon>
                      <FaSortAlphaUpAlt />
                    </Icon>
                  )}
                </Menu.Item>
              ))}
            </Menu.ItemGroup>
          </Menu.Content>
        </Menu.Positioner>
      </Portal>
    </Menu.Root>
  );
};

export const LibraryHeader = () => {
  const navigate = useNavigate({ from: Route.fullPath });

  const { type, search } = Route.useSearch();

  const redirect = (type: string) => {
    navigate({
      search: (prev: Record<string, string>) => ({
        ...prev,
        type,
      }),
    });
  };

  return (
    <Box p={4} w={'full'} shadow="lg" pos={'relative'}>
      <Spacer />
      <Flex alignItems="center" gap={2}>
        <Box>
          <SegmentGroup.Root
            defaultValue={type}
            onValueChange={(e) => redirect(e.value as string)}
            p={1}
            borderRadius={'md'}
            size={'sm'}
          >
            <SegmentGroup.Indicator top={1} left={1} />
            <SegmentGroup.Items
              style={{ cursor: 'pointer' }}
              items={[
                {
                  value: 'actions',
                  label: (
                    <HStack>
                      <BiTask />
                      {t('library.columnsHeader.actions')}
                    </HStack>
                  ),
                },
                {
                  value: 'indicators',
                  label: (
                    <HStack>
                      <GoGoal />
                      {t('library.columnsHeader.indicators')}
                    </HStack>
                  ),
                },
              ]}
            />
          </SegmentGroup.Root>
        </Box>
        <Box ml={'auto'}>
          <SearchInput search={search} />
        </Box>
        <Box>
          <LibraryFilterDrawer />
        </Box>
        <Box>
          <IndicatorsSortMenu />
        </Box>
      </Flex>
    </Box>
  );
};

export default LibraryHeader;
